import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import GetData from "../../services/getData";
import Button from "./Button";
import "./AiVoiceDictationModal.css";

const MAX_RECORDING_SECONDS = 300;
const MIN_BLOB_BYTES = 2000;

const STEPS = [
    { key: "record" },
    { key: "transcribe" },
    { key: "fill" },
];

/** Устанавливает значение по точечному пути, не меняя исходный объект. */
export const setFormValueByPath = (source, path, value) => {
    const keys = String(path ?? "")
        .split(".")
        .filter(Boolean);
    if (!keys.length) return source;

    const clone = { ...(source || {}) };
    let cursor = clone;
    for (let i = 0; i < keys.length - 1; i += 1) {
        const key = keys[i];
        const current = cursor[key];
        const next = Array.isArray(current) ? [...current] : { ...(current || {}) };
        cursor[key] = next;
        cursor = next;
    }
    cursor[keys[keys.length - 1]] = value;
    return clone;
};

const formatSeconds = (total) => {
    const value = Math.max(0, Math.floor(Number(total) || 0));
    const minutes = Math.floor(value / 60);
    const seconds = value % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const formatSize = (bytes, t) => {
    const value = Number(bytes) || 0;
    if (value < 1024) return t("voice.size.bytes", { value });
    if (value < 1024 * 1024) return t("voice.size.kb", { value: Math.round(value / 1024) });
    return t("voice.size.mb", { value: (value / 1048576).toFixed(1) });
};

const describeMicError = (error, t) => {
    const name = String(error?.name || "");
    if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        return t("voice.mic.denied");
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        return t("voice.mic.notFound");
    }
    if (name === "NotReadableError" || name === "TrackStartError") {
        return t("voice.mic.busy");
    }
    if (name === "SecurityError") {
        return t("voice.mic.security");
    }
    return error?.message || t("voice.mic.unknown");
};

const extractErrorMessage = (data, fallback, t) => {
    if (!data) return fallback;

    if (typeof data === "string") {
        const text = data.trim();
        if (!text) return fallback;
        if (text.includes("invalid module or block")) {
            return t("voice.errors.unknownOperation");
        }
        if (text.startsWith("<") || text.toLowerCase().includes("internal server error")) {
            return t("voice.errors.serverError");
        }
        return text;
    }

    if (data.isAxiosError) {
        const payload = data.response?.data;
        const serverMessage =
            payload?.request?.message || payload?.message || payload?.error;
        if (serverMessage) return String(serverMessage);
        if (data.code === "ECONNABORTED" || data.code === "ETIMEDOUT") {
            return t("voice.errors.timeout");
        }
        const code = Number(data.response?.status) || 0;
        if (code === 413) {
            return t("voice.errors.tooLarge");
        }
        if (code === 504 || code === 502) {
            return t("voice.errors.serverSlow");
        }
        if (code >= 500) {
            return t("voice.errors.internal");
        }
        return data.message ? String(data.message) : fallback;
    }

    if (data.message) return String(data.message);
    return fallback;
};

const pickRecorderMime = () => {
    if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
        return "";
    }
    const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
        "audio/mpeg",
    ];
    return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || "";
};

const extensionForMime = (mime) => {
    const value = String(mime || "").toLowerCase();
    if (value.includes("webm")) return "webm";
    if (value.includes("ogg") || value.includes("opus")) return "ogg";
    if (value.includes("mp4") || value.includes("m4a") || value.includes("aac")) return "mp4";
    if (value.includes("mpeg") || value.includes("mp3")) return "mp3";
    if (value.includes("wav")) return "wav";
    return "webm";
};

const PREVIEW_LIMIT = 160;

/**
 * Перекодирует запись в WAV 16 кГц моно.
 * Нужно на случай, если сервис распознавания не примет сжатый формат браузера.
 */
const encodeWavFromBlob = async (blob) => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx || typeof OfflineAudioContext === "undefined") return null;

    let decoded;
    const decodeContext = new AudioCtx();
    try {
        decoded = await decodeContext.decodeAudioData(await blob.arrayBuffer());
    } catch (_) {
        return null;
    } finally {
        try {
            decodeContext.close();
        } catch (_) {
            // контекст уже закрыт
        }
    }
    if (!decoded || !decoded.length) return null;

    const targetRate = 16000;
    const frameCount = Math.max(1, Math.ceil(decoded.duration * targetRate));
    const offline = new OfflineAudioContext(1, frameCount, targetRate);
    const source = offline.createBufferSource();
    source.buffer = decoded;
    source.connect(offline.destination);
    source.start(0);

    let rendered;
    try {
        rendered = await offline.startRendering();
    } catch (_) {
        return null;
    }

    const samples = rendered.getChannelData(0);
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    const writeString = (offset, text) => {
        for (let i = 0; i < text.length; i += 1) {
            view.setUint8(offset + i, text.charCodeAt(i));
        }
    };

    writeString(0, "RIFF");
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(8, "WAVE");
    writeString(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, targetRate, true);
    view.setUint32(28, targetRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, "data");
    view.setUint32(40, samples.length * 2, true);

    let offset = 44;
    for (let i = 0; i < samples.length; i += 1) {
        const sample = Math.max(-1, Math.min(1, samples[i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
        offset += 2;
    }

    return new Blob([view], { type: "audio/wav" });
};

const truncateValue = (value) => {
    const text = typeof value === "string" ? value : JSON.stringify(value ?? "");
    const normalized = String(text ?? "").replace(/\s+/g, " ").trim();
    if (normalized.length <= PREVIEW_LIMIT) return normalized;
    return `${normalized.slice(0, PREVIEW_LIMIT - 1)}…`;
};

const AiVoiceDictationModal = ({
    show,
    onHide,
    patientId,
    templateId,
    formData,
    onApply,
    onUndo,
}) => {
    const { t } = useTranslation("ui");
    // "idle" | "recording" | "transcribing" | "filling" | "done" | "error"
    const [stage, setStage] = useState("idle");
    const [errorText, setErrorText] = useState("");
    const [errorStage, setErrorStage] = useState("");
    const [elapsed, setElapsed] = useState(0);
    const [busyElapsed, setBusyElapsed] = useState(0);
    const [level, setLevel] = useState(0);
    const [transcript, setTranscript] = useState("");
    const [audioMeta, setAudioMeta] = useState(null);
    const [applied, setApplied] = useState([]);
    const [skipped, setSkipped] = useState([]);
    const [showTranscript, setShowTranscript] = useState(false);
    const [processingNote, setProcessingNote] = useState("");
    const [diagnostics, setDiagnostics] = useState(null);

    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const audioCtxRef = useRef(null);
    const rafRef = useRef(0);
    const tickRef = useRef(0);
    const autoStopRef = useRef(0);
    const busyTimerRef = useRef(0);
    const startedAtRef = useRef(0);
    const lastBlobRef = useRef(null);
    const abortedRef = useRef(false);
    const propsRef = useRef({});
    propsRef.current = { patientId, templateId, formData, onApply };
    const blobForRetryRef = useRef(null);

    const clearTimers = () => {
        window.cancelAnimationFrame(rafRef.current);
        window.clearInterval(tickRef.current);
        window.clearInterval(busyTimerRef.current);
        window.clearTimeout(autoStopRef.current);
        rafRef.current = 0;
        tickRef.current = 0;
        busyTimerRef.current = 0;
        autoStopRef.current = 0;
        setLevel(0);
    };

    const releaseMedia = useCallback(() => {
        clearTimers();
        if (recorderRef.current) {
            try {
                recorderRef.current.ondataavailable = null;
                recorderRef.current.onstop = null;
                if (recorderRef.current.state !== "inactive") {
                    recorderRef.current.stop();
                }
            } catch (_) {
                // слушатели уже сняты — ничего не делаем
            }
            recorderRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => {
                try {
                    track.stop();
                } catch (_) {
                    // трек уже остановлен
                }
            });
            streamRef.current = null;
        }
        if (audioCtxRef.current) {
            try {
                audioCtxRef.current.close();
            } catch (_) {
                // контекст уже закрыт
            }
            audioCtxRef.current = null;
        }
    }, []);

    const startBusyTimer = useCallback(() => {
        window.clearInterval(busyTimerRef.current);
        startedAtRef.current = Date.now();
        setBusyElapsed(0);
        busyTimerRef.current = window.setInterval(() => {
            setBusyElapsed((Date.now() - startedAtRef.current) / 1000);
        }, 500);
    }, []);

    const fail = useCallback((message, failedStage) => {
        if (abortedRef.current) return;
        window.clearInterval(busyTimerRef.current);
        busyTimerRef.current = 0;
        setStage("error");
        setErrorStage(failedStage || "");
        setErrorText(message);
    }, []);

    /** При сбое выясняем состояние серверного окружения — это показывает причину. */
    const runDiagnostics = useCallback(async () => {
        try {
            const response = await GetData(
                {
                    module: "patient",
                    block: "ai_voice_fill",
                    action: "diagnostics",
                    id_patient: propsRef.current.patientId,
                },
                null,
                { timeout: 60000 },
            );
            const problems = response?.result?.problems;
            if (Array.isArray(problems) && problems.length > 0) {
                setDiagnostics(problems);
            }
        } catch (_) {
            // диагностика необязательна
        }
    }, []);

    /** Отправляет аудио на распознавание и затем на заполнение формы. */
    const runPipeline = useCallback(
        async (blob) => {
            const current = propsRef.current;
            lastBlobRef.current = blob;

            const duration = Math.max(0, (Date.now() - startedAtRef.current) / 1000);

            if (abortedRef.current) return;

            setStage("transcribing");
            startBusyTimer();
            setProcessingNote("");

            const sendForTranscription = (audioBlob) => {
                const file = new File(
                    [audioBlob],
                    `dictation.${extensionForMime(audioBlob.type)}`,
                    { type: audioBlob.type || "audio/webm" },
                );
                return GetData(
                    {
                        module: "patient",
                        block: "ai_voice_fill",
                        action: "transcribe",
                        id_patient: current.patientId,
                    },
                    [file],
                    { timeout: 300000 },
                );
            };

            setAudioMeta({ seconds: Math.round(duration), size: blob.size });

            let transcribeResponse = null;
            try {
                transcribeResponse = await sendForTranscription(blob);
            } catch (error) {
                fail(
                    t("voice.errors.transcribeSendFailed", {
                        error: error?.message || t("voice.errors.network"),
                    }),
                    "transcribe",
                );
                return;
            }

            if (abortedRef.current) return;

            // Сервис мог не принять сжатый формат браузера — пробуем перекодировать в WAV
            if (!transcribeResponse?.result) {
                setProcessingNote(t("voice.errors.reencodeWav"));
                const wavBlob = await encodeWavFromBlob(blob);
                if (wavBlob && !abortedRef.current) {
                    try {
                        transcribeResponse = await sendForTranscription(wavBlob);
                    } catch (error) {
                        void error;
                    }
                }
                setProcessingNote("");
                if (abortedRef.current) return;
            }

            const recognizedText = transcribeResponse?.result?.transcript;
            if (!recognizedText) {
                fail(
                    extractErrorMessage(
                        transcribeResponse,
                        t("voice.errors.transcribeFailed"),
                        t,
                    ),
                    "transcribe",
                );
                return;
            }

            setTranscript(recognizedText);
            setStage("filling");
            startBusyTimer();

            let fillResponse;
            try {
                fillResponse = await GetData(
                    {
                        module: "patient",
                        block: "ai_voice_fill",
                        action: "fill",
                        id_patient: current.patientId,
                        template_id: current.templateId,
                        transcript: recognizedText,
                        values: current.formData ?? {},
                    },
                    null,
                    { timeout: 300000 },
                );
            } catch (error) {
                fail(
                    t("voice.errors.fillFailed", {
                        error: error?.message || t("voice.errors.network"),
                    }),
                    "fill",
                );
                return;
            }

            if (abortedRef.current) return;

            const result = fillResponse?.result;
            if (!result || !Array.isArray(result.applied)) {
                fail(
                    extractErrorMessage(
                        fillResponse,
                        t("voice.errors.fillEmpty"),
                        t,
                    ),
                    "fill",
                );
                return;
            }

            window.clearInterval(busyTimerRef.current);
            busyTimerRef.current = 0;

            const appliedItems = result.applied;
            setApplied(appliedItems);
            setSkipped(Array.isArray(result.skipped) ? result.skipped : []);

            if (appliedItems.length > 0 && typeof current.onApply === "function") {
                current.onApply(appliedItems);
            }

            setStage("done");
        },
        [fail, startBusyTimer],
    );

    const stopRecording = useCallback(
        () => {
            const recorder = recorderRef.current;
            window.clearInterval(tickRef.current);
            tickRef.current = 0;
            window.clearTimeout(autoStopRef.current);
            autoStopRef.current = 0;
            window.cancelAnimationFrame(rafRef.current);
            rafRef.current = 0;
            setLevel(0);

            if (!recorder || recorder.state === "inactive") {
                if (blobForRetryRef.current) return;
                fail(t("voice.errors.recordNotStarted"), "record");
                return;
            }

            try {
                recorder.stop();
            } catch (_) {
                fail(t("voice.errors.recordStopFailed"), "record");
            }
        },
        [fail],
    );

    const startRecording = useCallback(async () => {
        setStage("recording");
        setErrorText("");
        setErrorStage("");
        setTranscript("");
        setApplied([]);
        setSkipped([]);
        setAudioMeta(null);
        setElapsed(0);
        setBusyElapsed(0);
        setShowTranscript(false);
        setProcessingNote("");
        setDiagnostics(null);

        if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
            fail(t("voice.errors.noMicApi"), "record");
            return;
        }
        if (typeof MediaRecorder === "undefined") {
            fail(t("voice.errors.noMediaRecorder"), "record");
            return;
        }

        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
        } catch (error) {
            fail(describeMicError(error, t), "record");
            return;
        }

        if (abortedRef.current) {
            stream.getTracks().forEach((track) => track.stop());
            return;
        }

        streamRef.current = stream;

        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx) {
                const context = new AudioCtx();
                audioCtxRef.current = context;
                const analyser = context.createAnalyser();
                analyser.fftSize = 512;
                analyser.smoothingTimeConstant = 0.7;
                context.createMediaStreamSource(stream).connect(analyser);
                const buffer = new Uint8Array(analyser.frequencyBinCount);
                const tick = () => {
                    analyser.getByteTimeDomainData(buffer);
                    let sum = 0;
                    for (let i = 0; i < buffer.length; i += 1) {
                        const value = (buffer[i] - 128) / 128;
                        sum += value * value;
                    }
                    const rms = Math.sqrt(sum / buffer.length);
                    setLevel(Math.min(1, Math.max(0, rms * 3.4)));
                    rafRef.current = window.requestAnimationFrame(tick);
                };
                rafRef.current = window.requestAnimationFrame(tick);
            }
        } catch (_) {
            // индикатор уровня необязателен — запись продолжается
        }

        const mimeType = pickRecorderMime();
        let recorder = null;
        try {
            recorder = mimeType
                ? new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 64000 })
                : new MediaRecorder(stream);
        } catch (_) {
            try {
                recorder = new MediaRecorder(stream);
            } catch (error) {
                releaseMedia();
                fail(t("voice.errors.recorderInitFailed"), "record");
                return;
            }
        }

        chunksRef.current = [];
        recorder.ondataavailable = (event) => {
            if (event?.data && event.data.size > 0) {
                chunksRef.current.push(event.data);
            }
        };
        recorder.onstop = () => {
            const chunks = chunksRef.current;
            chunksRef.current = [];
            const type = recorder.mimeType || chunks[0]?.type || "audio/webm";
            releaseMedia();

            const blob = new Blob(chunks, { type });
            if (abortedRef.current) return;
            if (!blob.size || blob.size < MIN_BLOB_BYTES) {
                fail(t("voice.errors.tooShort"), "record");
                return;
            }
            blobForRetryRef.current = blob;
            void runPipeline(blob);
        };

        recorderRef.current = recorder;
        startedAtRef.current = Date.now();
        setElapsed(0);

        try {
            recorder.start(1000);
        } catch (error) {
            releaseMedia();
            fail(t("voice.errors.startFailed"), "record");
            return;
        }

        tickRef.current = window.setInterval(() => {
            setElapsed((Date.now() - startedAtRef.current) / 1000);
        }, 200);

        autoStopRef.current = window.setTimeout(() => {
            stopRecording(true);
        }, MAX_RECORDING_SECONDS * 1000);
    }, [fail, releaseMedia, runPipeline, stopRecording]);

    const restart = useCallback(() => {
        releaseMedia();
        blobForRetryRef.current = null;
        void startRecording();
    }, [releaseMedia, startRecording]);

    const retryProcessing = useCallback(() => {
        const blob = blobForRetryRef.current;
        if (!blob) {
            void startRecording();
            return;
        }
        setErrorText("");
        setErrorStage("");
        void runPipeline(blob);
    }, [runPipeline, startRecording]);

    const handleClose = useCallback(
        (withUndo) => {
            abortedRef.current = true;
            releaseMedia();
            if (withUndo && typeof onUndo === "function") {
                onUndo();
            }
            onHide?.();
        },
        [onHide, onUndo, releaseMedia],
    );

    useEffect(() => {
        if (!show) {
            abortedRef.current = true;
            releaseMedia();
            return undefined;
        }

        abortedRef.current = false;
        blobForRetryRef.current = null;
        void startRecording();

        return () => {
            abortedRef.current = true;
            releaseMedia();
        };
        // startRecording намеренно не в зависимостях: запись стартует только при открытии окна
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    // При сбое однократно опрашиваем окружение сервера, чтобы показать причину
    useEffect(() => {
        if (stage === "error") {
            void runDiagnostics();
        }
    }, [stage, runDiagnostics]);

    useEffect(() => {
        if (!show) {
            setDiagnostics(null);
        }
    }, [show]);

    const isBusy = stage === "recording" || stage === "transcribing" || stage === "filling";

    const stepStatus = (index) => {
        if (stage === "error") {
            const failedIndex = STEPS.findIndex((step) => step.key === errorStage);
            if (failedIndex === -1) return index === 0 ? "error" : "pending";
            if (index < failedIndex) return "done";
            return index === failedIndex ? "error" : "pending";
        }
        if (stage === "done") return "done";
        if (stage === "recording") return index === 0 ? "active" : "pending";
        if (stage === "transcribing") return index === 0 ? "done" : index === 1 ? "active" : "pending";
        if (stage === "filling") return index < 2 ? "done" : "active";
        return "pending";
    };

    const renderStep = (step, index) => {
        const status = stepStatus(index);
        return (
            <div key={step.key} className={`ai-voice-step ai-voice-step--${status}`}>
                <div className="ai-voice-step-marker">
                    {status === "done" && (
                        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                            <path
                                d="M9.6 16.9 4.7 12l1.4-1.4 3.5 3.5 8.3-8.3L19.3 7z"
                                fill="currentColor"
                            />
                        </svg>
                    )}
                    {status === "active" && <span className="ai-voice-step-spinner" />}
                    {status === "error" && (
                        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                            <path
                                d="M12 2 1 21h22zm1 14h-2v2h2zm0-7h-2v5h2z"
                                fill="currentColor"
                            />
                        </svg>
                    )}
                    {status === "pending" && <span className="ai-voice-step-dot" />}
                </div>
                <div className="ai-voice-step-body">
                    <div className="ai-voice-step-title">
                        {t(`voice.steps.${step.key}.title`)}
                    </div>
                    <div className="ai-voice-step-hint">
                        {t(`voice.steps.${step.key}.hint`)}
                    </div>
                    {index === 0 && (stage === "recording" || stage === "transcribing" || stage === "filling" || stage === "done") && audioMeta != null && (
                        <div className="ai-voice-step-meta">
                            {t("voice.metaDuration", {
                                time: formatSeconds(audioMeta.seconds),
                                size: formatSize(audioMeta.size, t),
                            })}
                        </div>
                    )}
                    {index === 1 && stage !== "recording" && stage !== "pending" && transcript && (
                        <div className="ai-voice-step-meta">
                            {t("voice.metaChars", { count: transcript.length })}
                        </div>
                    )}
                    {index === 2 && stage === "done" && (
                        <div className="ai-voice-step-meta">
                            {t("voice.metaFields", { count: applied.length })}
                            {skipped.length > 0
                                ? t("voice.metaFieldsSkipped", { count: skipped.length })
                                : ""}
                        </div>
                    )}
                    {(index === 1 || index === 2) && status === "active" && (
                        <div className="ai-voice-step-meta">
                            {t("voice.busyNote", { seconds: Math.floor(busyElapsed) })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Modal
            show={show}
            onHide={() => handleClose(false)}
            centered
            dialogClassName="ai-voice-dialog">
            <Modal.Header closeButton={!isBusy || stage === "recording"}>
                <Modal.Title className="ai-voice-title">
                    <span className="ai-voice-title-icon">
                        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                            <path
                                d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-2.08A7 7 0 0 0 19 12z"
                                fill="currentColor"
                            />
                        </svg>
                    </span>
                    {t("voice.title")}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {stage === "recording" && (
                    <div className="ai-voice-recorder">
                        <div
                            className="ai-voice-mic"
                            style={{
                                transform: `scale(${1 + Math.min(level, 1) * 0.12})`,
                            }}>
                            <span className="ai-voice-mic-ring ai-voice-mic-ring--1" />
                            <span className="ai-voice-mic-ring ai-voice-mic-ring--2" />
                            <span className="ai-voice-mic-ring ai-voice-mic-ring--3" />
                            <div className="ai-voice-mic-circle">
                                <svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true">
                                    <path
                                        d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-2.08A7 7 0 0 0 19 12z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </div>
                        </div>

                        <div className="ai-voice-timer">{formatSeconds(elapsed)}</div>

                        <div className="ai-voice-level">
                            {[0, 1, 2, 3, 4].map((bar) => {
                                const threshold = (bar + 1) / 5;
                                const active = level >= threshold;
                                return (
                                    <span
                                        key={bar}
                                        className={`ai-voice-level-bar${active ? " is-active" : ""}`}
                                        style={{
                                            height: `${10 + bar * 6}px`,
                                            transform: active
                                                ? `scaleY(${Math.min(1, level * 1.6)})`
                                                : "scaleY(0.35)",
                                        }}
                                    />
                                );
                            })}
                        </div>

                        <div className="ai-voice-status">
                            {t("voice.statusRecorder", {
                                max: formatSeconds(MAX_RECORDING_SECONDS),
                            })}
                        </div>
                    </div>
                )}

                {(stage === "transcribing" || stage === "filling") && (
                    <div className="ai-voice-processing">
                        <div className="ai-voice-processing-spinner" />
                        <div className="ai-voice-processing-title">
                            {stage === "transcribing"
                                ? t("voice.processing.transcribeTitle")
                                : t("voice.processing.fillTitle")}
                        </div>
                        <div className="ai-voice-processing-note">
                            {stage === "transcribing"
                                ? t("voice.processing.transcribeNote")
                                : t("voice.processing.fillNote")}
                        </div>
                        {processingNote && (
                            <div className="ai-voice-processing-hint">{processingNote}</div>
                        )}
                    </div>
                )}

                {stage === "done" && (
                    <div className="ai-voice-result">
                        <div
                            className={`ai-voice-result-headline${
                                applied.length ? " is-success" : " is-warning"
                            }`}>
                            {applied.length
                                ? t("voice.result.fieldsFilled", { count: applied.length })
                                : t("voice.result.nothingFound")}
                        </div>

                        {applied.length > 0 && (
                            <>
                                <div className="ai-voice-result-note">
                                    {t("voice.result.noteApplied")}
                                </div>
                                <ul className="ai-voice-result-list">
                                    {applied.map((item) => (
                                        <li key={item.path}>
                                            <span className="ai-voice-result-label">
                                                {item.label}
                                            </span>
                                            <span className="ai-voice-result-value">
                                                {truncateValue(item.value)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}

                        {applied.length === 0 && (
                            <div className="ai-voice-result-note">
                                {t("voice.result.noteEmpty")}
                            </div>
                        )}

                        {skipped.length > 0 && (
                            <div className="ai-voice-result-skipped">
                                {t("voice.result.skipped", {
                                    count: skipped.length,
                                    label: skipped[0].label,
                                    reason: skipped[0].reason,
                                })}
                            </div>
                        )}
                    </div>
                )}

                {stage === "error" && (
                    <div className="ai-voice-error">
                        <div className="ai-voice-error-title">{t("voice.error.title")}</div>
                        <div className="ai-voice-error-text">{errorText}</div>
                        {Array.isArray(diagnostics) && diagnostics.length > 0 && (
                            <div className="ai-voice-error-checks">
                                <div className="ai-voice-error-checks-title">
                                    {t("voice.error.diagnostics")}
                                </div>
                                <ul>
                                    {diagnostics.map((item) => (
                                        <li key={item}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {(stage !== "recording" || transcript) && (
                    <div className="ai-voice-steps">{STEPS.map(renderStep)}</div>
                )}

                {transcript && (
                    <div className="ai-voice-transcript">
                        <button
                            type="button"
                            className="ai-voice-transcript-toggle"
                            onClick={() => setShowTranscript((prev) => !prev)}>
                            {showTranscript ? t("voice.transcript.hide") : t("voice.transcript.show")}
                        </button>
                        {showTranscript && (
                            <div className="ai-voice-transcript-body">{transcript}</div>
                        )}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                {stage === "recording" && (
                    <>
                        <Button
                            onClick={() => handleClose(false)}
                            variant="secondary">
                            {t("voice.buttons.cancel")}
                        </Button>
                        <Button
                            onClick={() => stopRecording(false)}
                            variant="primary">
                            {t("voice.buttons.stopAndProcess")}
                        </Button>
                    </>
                )}

                {(stage === "transcribing" || stage === "filling") && (
                    <Button
                        onClick={() => handleClose(false)}
                        variant="secondary">
                        {t("voice.buttons.close")}
                    </Button>
                )}

                {stage === "done" && (
                    <>
                        <Button onClick={restart} variant="secondary">
                            {t("voice.buttons.restart")}
                        </Button>
                        {applied.length > 0 && (
                            <Button
                                onClick={() => handleClose(true)}
                                variant="danger-ghost">
                                {t("voice.buttons.undoApply")}
                            </Button>
                        )}
                        <Button
                            onClick={() => handleClose(false)}
                            variant="primary">
                            {t("voice.buttons.done")}
                        </Button>
                    </>
                )}

                {stage === "error" && (
                    <>
                        <Button
                            onClick={retryProcessing}
                            variant="secondary">
                            {t("voice.buttons.retryProcessing")}
                        </Button>
                        <Button onClick={restart} variant="secondary">
                            {t("voice.buttons.restart")}
                        </Button>
                        <Button
                            onClick={() => handleClose(false)}
                            variant="secondary">
                            {t("voice.buttons.close")}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default AiVoiceDictationModal;
